import React, { useState } from 'react';
import { Modal, Select, InputNumber, Button, Table, Typography, Space, notification } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { matchesAllSearchWords, formatNumber, formatMoney } from './orderFormHelpers';
import { hasOwnPrice, componentValue, assortmentValuePerBox, lineSubtotal, resolveBasePrice } from './orderLineValue';

const { Text } = Typography;

// Etiqueta legible de una variante a partir de sus atributos.
export const buildVariantLabel = (variant) =>
  variant?.attributes?.map(a => a.value?.value).filter(Boolean).join(' ') || '';

// Normaliza los `components` que devuelve el GET de la orden al shape que
// maneja este modal (ids como string + campos de display). El component_id
// viaja con el componente para que el backend lo actualice en su lugar.
export const mapServerComponents = (serverComponents) =>
  (serverComponents || []).map(c => ({
    component_id: String(c.component_id),
    component_product_id: String(c.component_product_id),
    component_variant_id: c.component_variant_id != null ? String(c.component_variant_id) : null,
    bunches: Number(c.bunches),
    stems_per_bunch: Number(c.stems_per_bunch),
    // null hereda el precio de la línea.
    unit_price: c.unit_price === null || c.unit_price === undefined ? null : Number(c.unit_price),
    product_name: c.component_product?.name || c.product_name_snapshot || '',
    variant_label: buildVariantLabel(c.component_variant),
  }));

// Deja solo los campos que el backend espera, sin los de display. Los
// componentes nuevos no llevan component_id: el backend los crea.
export const toPayloadComponents = (components) =>
  (components || []).map(c => ({
    ...(c.component_id ? { component_id: c.component_id } : {}),
    component_product_id: c.component_product_id,
    component_variant_id: c.component_variant_id,
    bunches: Number(c.bunches),
    stems_per_bunch: Number(c.stems_per_bunch),
    ...(c.unit_price !== undefined ? { unit_price: c.unit_price } : {}),
  }));

export const sumBunches = (components) =>
  (components || []).reduce((acc, c) => acc + Number(c.bunches || 0), 0);

export const sumStems = (components) =>
  (components || []).reduce((acc, c) => acc + Number(c.bunches || 0) * Number(c.stems_per_bunch || 0), 0);

const labelStyle = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.8,
  color: 'var(--fc-text-secondary)',
  fontWeight: 500,
};

/**
 * Armador del surtido de UNA caja. El estado vive aquí mientras el modal está
 * abierto y se entrega al padre en onSave; el padre es quien lo persiste al
 * agregar o guardar la línea.
 */
// billingUnit, linePrice y quantity son los de la línea. Por tallo cada
// componente lleva su precio y el de la línea se calcula de ellos.
//
// onSave(components, result): por tallo, `result` trae { linePrice }, el precio
// que cubre más tallos de la caja.
const AssortmentModal = ({ open, onCancel, onSave, initialComponents, allProducts, isReadOnly, billingUnit, linePrice, quantity, currency }) => {
  const [rows, setRows] = useState(initialComponents || []);

  const pricedByStem = billingUnit === 'TALLO';
  const lineHasPrice = linePrice !== null && linePrice !== undefined && linePrice !== '';

  const [draftKey, setDraftKey] = useState(null);
  // La lista de variantes solo se abre cuando hay algo escrito.
  const [variantSearch, setVariantSearch] = useState('');
  const [draftBunches, setDraftBunches] = useState(null);
  const [draftStems, setDraftStems] = useState(null);
  // El precio de la fila de agregar es solo ayuda de captura: arranca con el
  // último usado en el surtido o, si no hay, con el de la línea.
  const [draftPrice, setDraftPrice] = useState(() => {
    const lastPriced = [...(initialComponents || [])].reverse().find(hasOwnPrice);
    if (lastPriced) return Number(lastPriced.unit_price);
    return lineHasPrice ? Number(linePrice) : null;
  });
  const [applyAllPrice, setApplyAllPrice] = useState(null);

  // Un componente siempre necesita variante, así que la lista se arma
  // directamente sobre las variantes; los productos sin variantes quedan fuera.
  // La etiqueta lleva el nombre completo "producto + atributos", igual que el
  // Select de producto de la barra de captura.
  const variantOptions = (allProducts || [])
    .filter(p => p.variants && p.variants.length > 0)
    .flatMap(p =>
      p.variants.map(v => {
        const attrStr = buildVariantLabel(v);
        return {
          value: `${p.product_id}_${v.variant_id}`,
          label: attrStr ? `${p.name} ${attrStr}` : p.name,
          product_id: String(p.product_id),
          variant_id: String(v.variant_id),
          product_name: p.name,
          variant_label: attrStr,
        };
      })
    );

  const resetDraft = () => {
    setDraftKey(null);
    setDraftBunches(null);
    setDraftStems(null);
  };

  const handleAddComponent = () => {
    const option = variantOptions.find(o => o.value === draftKey);
    if (!option) {
      notification.error({ message: 'Elige una variante' });
      return;
    }
    if (!draftBunches || draftBunches <= 0 || !draftStems || draftStems <= 0) {
      notification.error({ message: 'Ramos y tallos/ramo deben ser mayores a 0' });
      return;
    }
    if (pricedByStem && !hasOwnPrice({ unit_price: draftPrice })) {
      notification.error({ message: 'Cada componente necesita un precio.' });
      return;
    }

    setRows(prev => [
      ...prev,
      {
        component_product_id: option.product_id,
        component_variant_id: option.variant_id,
        bunches: Number(draftBunches),
        stems_per_bunch: Number(draftStems),
        ...(pricedByStem ? { unit_price: Number(draftPrice) } : {}),
        product_name: option.product_name,
        variant_label: option.variant_label,
      },
    ]);
    // El precio se queda: es el último usado para el siguiente componente.
    resetDraft();
  };

  // Asigna un mismo precio a todos los componentes, a la vista; se guarda con Guardar.
  const applyPriceToAll = () => {
    setRows(prev => prev.map(row => ({ ...row, unit_price: Number(applyAllPrice) })));
  };

  const handleRemoveComponent = (index) => {
    setRows(prev => prev.filter((_, i) => i !== index));
  };

  // Editar en la misma fila conserva el component_id y el precio ajustado.
  const updateComponent = (index, changes) => {
    setRows(prev => prev.map((row, i) => (i === index ? { ...row, ...changes } : row)));
  };

  // Guardar sin componentes deja la línea como caja normal (así se quita un
  // surtido existente).
  const handleSave = () => {
    if (rows.some(r => !r.bunches || Number(r.bunches) <= 0)) {
      notification.error({ message: 'Los ramos de cada componente deben ser mayores a 0' });
      return;
    }
    if (!pricedByStem || rows.length === 0) {
      onSave(rows);
      return;
    }
    if (rows.some(r => !hasOwnPrice(r))) {
      notification.error({ message: 'Cada componente necesita un precio.' });
      return;
    }
    onSave(rows, { linePrice: resolveBasePrice(rows) });
  };

  const totalBunches = sumBunches(rows);
  const totalStems = sumStems(rows);

  const componentLabel = (r) => `${r.product_name || ''} ${r.variant_label || ''}`.trim();
  const boxes = Number(quantity) || 0;
  const lineValue = lineSubtotal({ billing_unit: 'TALLO', unit_price: linePrice, quantity: boxes }, rows);

  const columns = [
    {
      title: 'PRODUCTO',
      key: 'producto',
      render: (_, r) => <Text strong>{r.product_name || '—'}</Text>,
    },
    {
      title: 'VARIANTE',
      key: 'variante',
      render: (_, r) => r.variant_label || <Text type="secondary">—</Text>,
    },
    {
      title: 'RAMOS',
      key: 'bunches',
      width: 76,
      align: 'right',
      render: (_, r, index) =>
        isReadOnly ? r.bunches : (
          <InputNumber
            aria-label={`Ramos de ${componentLabel(r)}`}
            size="small"
            min={1}
            precision={0}
            controls={false}
            value={r.bunches}
            onChange={(v) => updateComponent(index, { bunches: v })}
            style={{ width: 60 }}
          />
        ),
    },
    {
      title: 'TALLOS/RAMO',
      dataIndex: 'stems_per_bunch',
      width: 100,
      align: 'right',
    },
    {
      title: 'TALLOS',
      key: 'tallos',
      width: 70,
      align: 'right',
      render: (_, r) => (
        <Text strong>{((Number(r.bunches) || 0) * Number(r.stems_per_bunch)).toLocaleString('es-CO')}</Text>
      ),
    },
    ...(pricedByStem ? [
      {
        title: 'PRECIO',
        key: 'unit_price',
        width: 96,
        render: (_, r, index) => {
          if (isReadOnly) return formatNumber(r.unit_price);
          return (
            <InputNumber
              aria-label={`Precio de ${componentLabel(r)}`}
              size="small"
              min={0}
              step={0.01}
              precision={2}
              controls={false}
              status={hasOwnPrice(r) ? undefined : 'error'}
              value={r.unit_price}
              onChange={(v) => updateComponent(index, { unit_price: v })}
              style={{ width: 80 }}
            />
          );
        },
      },
      {
        title: 'VALOR',
        key: 'value',
        width: 76,
        align: 'right',
        render: (_, r) => formatNumber(componentValue(r, linePrice)),
      },
    ] : []),
    {
      title: '',
      key: 'acciones',
      width: 50,
      render: (_, __, index) =>
        isReadOnly ? null : (
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleRemoveComponent(index)}
          />
        ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      destroyOnHidden
      width={860}
      title="Surtido de la caja"
      footer={
        isReadOnly
          ? [<Button key="close" onClick={onCancel}>Cerrar</Button>]
          : [
              <Button key="cancel" onClick={onCancel}>Cancelar</Button>,
              <Button key="save" type="primary" onClick={handleSave}>Guardar</Button>,
            ]
      }
    >
      {!isReadOnly && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: pricedByStem ? '2fr 1fr 1fr 1fr auto' : '2fr 1fr 1fr auto',
            gap: 12,
            alignItems: 'end',
            padding: '4px 0 16px',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={labelStyle}>VARIANTE</span>
            <Select
              showSearch={{ searchValue: variantSearch, onSearch: setVariantSearch, filterOption: matchesAllSearchWords }}
              open={variantSearch.trim().length > 0}
              onOpenChange={(nextOpen) => { if (!nextOpen) setVariantSearch(''); }}
              popupMatchSelectWidth={false}
              placeholder="Buscar variante..."
              value={draftKey}
              onChange={(val) => setDraftKey(val)}
              options={variantOptions}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={labelStyle}>RAMOS</span>
            <InputNumber
              aria-label="Ramos del nuevo componente"
              min={1}
              precision={0}
              value={draftBunches}
              onChange={(v) => setDraftBunches(v)}
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={labelStyle}>TALLOS/RAMO</span>
            <InputNumber
              aria-label="Tallos por ramo del nuevo componente"
              min={1}
              precision={0}
              value={draftStems}
              onChange={(v) => setDraftStems(v)}
              style={{ width: '100%' }}
            />
          </div>

          {pricedByStem && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={labelStyle}>PRECIO</span>
              <InputNumber
                aria-label="Precio del nuevo componente"
                min={0}
                step={0.01}
                precision={2}
                controls={false}
                value={draftPrice}
                onChange={(v) => setDraftPrice(v)}
                style={{ width: '100%' }}
              />
            </div>
          )}

          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddComponent}>
            Agregar componente
          </Button>
        </div>
      )}

      {/* Sin unidad (vistas de solo lectura de documentos viejos) no hay nada que explicar. */}
      {billingUnit && !pricedByStem && (
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
          El precio por componente solo aplica a líneas cobradas por tallo.
        </Text>
      )}

      {pricedByStem && !isReadOnly && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={labelStyle}>Precio para todos</span>
          <InputNumber
            aria-label="Precio para todos"
            size="small"
            min={0}
            step={0.01}
            precision={2}
            controls={false}
            value={applyAllPrice}
            onChange={(v) => setApplyAllPrice(v)}
            style={{ width: 80 }}
          />
          <Button size="small" onClick={applyPriceToAll} disabled={!hasOwnPrice({ unit_price: applyAllPrice }) || rows.length === 0}>
            Aplicar a todos
          </Button>
        </div>
      )}

      <Table
        columns={columns}
        dataSource={rows}
        rowKey={(_, index) => index}
        pagination={false}
        size="small"
        locale={{ emptyText: 'Sin componentes todavía' }}
      />

      <div aria-label="Totales del surtido" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
        <Space size={8} wrap>
          <span style={{ ...labelStyle, fontSize: 10 }}>Ramos/caja</span>
          <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>
            {totalBunches.toLocaleString('es-CO')}
          </Text>
          <span style={{ color: 'var(--fc-border)' }}>|</span>
          <span style={{ ...labelStyle, fontSize: 10, color: 'var(--fc-accent)' }}>Tallos/caja</span>
          <Text strong style={{ color: 'var(--fc-accent)', fontVariantNumeric: 'tabular-nums' }}>
            {totalStems.toLocaleString('es-CO')}
          </Text>
          {pricedByStem && (
            <>
              <span style={{ color: 'var(--fc-border)' }}>|</span>
              <span style={{ ...labelStyle, fontSize: 10 }}>Valor/caja</span>
              <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatMoney(assortmentValuePerBox(rows, linePrice), currency)}
              </Text>
            </>
          )}
          {/* El modal es de una caja; con varias, el valor de la línea completa
              es el que se compara con el subtotal. */}
          {pricedByStem && boxes > 1 && (
            <>
              <span style={{ color: 'var(--fc-border)' }}>|</span>
              <span style={{ ...labelStyle, fontSize: 10 }}>Valor línea ({boxes} cajas)</span>
              <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatMoney(lineValue, currency)}
              </Text>
            </>
          )}
        </Space>
      </div>
    </Modal>
  );
};

export default AssortmentModal;
